import React, { useRef } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, TextField, Switch, FormControlLabel, Select, MenuItem } from "@mui/material";
import { Label, Field, SwitchIcon } from "qwanyx";

export const Link = ({
  name = "",
  iconSize = 18,
  fontSize = "16px",
  value = "",
  qwantumId = null,
  type = "website", // Determines URL scheme and icon
  labelText = "Default Label",
  placeholder = "Enter text here...",
  onSave,
  labelWidth = "100%",
  width = "100%",
  closeOnComplete = false,
  editable = true,
  align = "left",
  layout = "column",
  labelAlign,
  showColon = true,
  style = {},
  hidden = false,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const fieldRef = useRef(null);

  // Determine alignment based on layout
  const effectiveLabelAlign = labelAlign || (layout === "column" ? "left" : "right");
  const icons = ['external-link', 'email', 'phone', 'website', 'import'];

  const onGoToLink = () => {
    const fieldValue = fieldRef.current?.getValue();
    if (!fieldValue) {
      console.error("Field value is empty. Cannot navigate.");
      return;
    }

    let url = "";
    switch (type) {
      case "email":
        url = `mailto:${fieldValue}`;
        break;
      case "phone":
        url = `tel:${fieldValue}`;
        break;
      case "website":
        url = fieldValue.startsWith("http") ? fieldValue : `https://${fieldValue}`;
        break;
      default:
        url = fieldValue.startsWith("http") ? fieldValue : `https://${fieldValue}`;
    }
    window.open(url, "_blank");
  };

  if (hidden) return null;

  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        width: "100%",
        minHeight: "40px",
        ...getSelectionStyle()
      }}
      {...props}
    >
      <div
        style={{
          display: "flex",
          flexDirection: layout,
          justifyContent: "flex-start",
          alignItems: layout === "row" ? "center" : "flex-start",
          gap: layout === "column" ? "4px" : "0",
          width: width,
          ...style,
        }}
      >
        {/* Label and Colon */}
        <div
          style={{
            width: width,
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            minWidth: labelWidth,
            justifyContent: effectiveLabelAlign === "left" ? "flex-start" : "flex-end",
            padding: 0,
            margin: 0,
          }}
        >
          <Label
            text={labelText}
            align={effectiveLabelAlign}
            style={{
              fontSize: fontSize,
              padding: 0,
              margin: 0,
            }}
          />
          {showColon && (
            <span
              style={{
                fontSize: fontSize,
                color: "black",
                marginLeft: "4px",
                verticalAlign: "middle",
              }}
            >
              :
            </span>
          )}
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexGrow: 1,
            alignItems: "right",
            justifyContent: "space-between",
            gap: "4px",
          }}
        >
          {/* Input Field */}
          <Field
            name={name}
            fontSize={fontSize}
            value={value}
            qwantumId={qwantumId}
            placeholder={placeholder}
            onSave={onSave}
            closeOnComplete={closeOnComplete}
            editable={editable}
            align={align}
            ref={fieldRef}
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: "0%",
              width: "100%",
            }}
          />
          <SwitchIcon
            iconNames={[icons[icons.indexOf(type)] || icons[0]]}
            size={iconSize}
            onClick={onGoToLink}
            id="link-icon"
          />
        </div>
      </div>
    </div>
  );
};

const LinkSettings = () => {
  const { 
    actions: { setProp }, 
    name,
    iconSize,
    fontSize,
    value,
    qwantumId,
    type,
    labelText,
    placeholder,
    labelWidth,
    width,
    closeOnComplete,
    editable,
    align,
    layout,
    labelAlign,
    showColon,
    style
  } = useNode((node) => ({
    name: node.data.props.name,
    iconSize: node.data.props.iconSize || 18,
    fontSize: node.data.props.fontSize || "16px",
    value: node.data.props.value || "",
    qwantumId: node.data.props.qwantumId,
    type: node.data.props.type || "website",
    labelText: node.data.props.labelText || "Default Label",
    placeholder: node.data.props.placeholder || "Enter text here...",
    labelWidth: node.data.props.labelWidth || "100%",
    width: node.data.props.width || "100%",
    closeOnComplete: node.data.props.closeOnComplete || false,
    editable: node.data.props.editable !== false,
    align: node.data.props.align || "left",
    layout: node.data.props.layout || "column",
    labelAlign: node.data.props.labelAlign,
    showColon: node.data.props.showColon !== false,
    style: node.data.props.style || {}
  }));

  const typeOptions = ['external-link', 'email', 'phone', 'website', 'import'];
  const alignOptions = ['left', 'center', 'right'];
  const layoutOptions = ['row', 'column'];

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Label Text</FormLabel>
        <TextField
          value={labelText}
          onChange={(e) => setProp(props => props.labelText = e.target.value)}
          size="small"
          placeholder="Label text"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Value</FormLabel>
        <TextField
          value={value}
          onChange={(e) => setProp(props => props.value = e.target.value)}
          size="small"
          placeholder="Field value"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Placeholder</FormLabel>
        <TextField
          value={placeholder}
          onChange={(e) => setProp(props => props.placeholder = e.target.value)}
          size="small"
          placeholder="Placeholder text"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Type (URL scheme & icon)</FormLabel>
        <Select
          value={type}
          onChange={(e) => setProp(props => props.type = e.target.value)}
          size="small"
        >
          {typeOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Qwantum ID (Database Key)</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value || null)}
          size="small"
          placeholder="Database persistence key"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Layout</FormLabel>
        <Select
          value={layout}
          onChange={(e) => setProp(props => props.layout = e.target.value)}
          size="small"
        >
          {layoutOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Align</FormLabel>
        <Select
          value={align}
          onChange={(e) => setProp(props => props.align = e.target.value)}
          size="small"
        >
          {alignOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Label Align</FormLabel>
        <Select
          value={labelAlign || ''}
          onChange={(e) => setProp(props => props.labelAlign = e.target.value || undefined)}
          size="small"
          displayEmpty
        >
          <MenuItem value="">Auto</MenuItem>
          {alignOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Size</FormLabel>
        <TextField
          value={fontSize}
          onChange={(e) => setProp(props => props.fontSize = e.target.value)}
          size="small"
          placeholder="16px, 1rem, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Icon Size</FormLabel>
        <TextField
          type="number"
          value={iconSize}
          onChange={(e) => setProp(props => props.iconSize = parseInt(e.target.value) || 18)}
          size="small"
          inputProps={{ min: 8, max: 64 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Label Width</FormLabel>
        <TextField
          value={labelWidth}
          onChange={(e) => setProp(props => props.labelWidth = e.target.value)}
          size="small"
          placeholder="100%, 200px, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={width}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          size="small"
          placeholder="100%, 300px, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Style JSON</FormLabel>
        <TextField
          multiline
          rows={3}
          value={JSON.stringify(style, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setProp(props => props.style = parsed);
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          size="small"
          placeholder='{"padding": "10px", "backgroundColor": "#f5f5f5"}'
        />
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={editable}
            onChange={(e) => setProp(props => props.editable = e.target.checked)}
          />
        }
        label="Editable"
      />

      <FormControlLabel
        control={
          <Switch
            checked={showColon}
            onChange={(e) => setProp(props => props.showColon = e.target.checked)}
          />
        }
        label="Show Colon"
      />

      <FormControlLabel
        control={
          <Switch
            checked={closeOnComplete}
            onChange={(e) => setProp(props => props.closeOnComplete = e.target.checked)}
          />
        }
        label="Close on Complete"
      />
    </div>
  );
};

Link.craft = {
  props: {
    name: "",
    iconSize: 18,
    fontSize: "16px",
    value: "",
    qwantumId: null,
    type: "website",
    labelText: "Default Label",
    placeholder: "Enter text here...",
    labelWidth: "100%",
    width: "100%",
    closeOnComplete: false,
    editable: true,
    align: "left",
    layout: "column",
    labelAlign: undefined,
    showColon: true,
    style: {}
  },
  related: {
    settings: LinkSettings
  }
};

export default Link;